import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { UserId } from '@business/entities/account';
import { ChecklistTemplateResponse, ChecklistId, SortByOptions, SortByDirections, CompactChecklistResponse } from '@business/entities/checklist';
import { TaskId, TaskResponse, TodoTaskStatus } from '@business/entities/task';
import { LoadingIndicator } from '@core/components/LoadingIndicator';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { Session } from '@modules/auth/session';
import { SortableList } from '@modules/common/Sortable';
import { openAddEditChecklistTemplateModal } from '@modules/todoTemplates/AddEditChecklistTemplateModal';
import { SortEnd } from 'react-sortable-hoc';
import { Card, CardBody, Row, Col } from 'reactstrap';
import { AddTask } from './AddTask';
import { ChecklistItem } from './ChecklistList/ChecklistItem';
import { TasksMenuBloc } from './TasksMenuBloc';
import { useHistory } from '@core/router';
import { Button } from '@core/components/Button';
import { TaskStatusResponse } from '@business/entities/task_status';
import { useAccountSettings } from '@core/utils/hooks/misc/useSettings';
import { OrgId } from '@business/entities/organization';
import { R } from '@core/utils/r';
import { Sortable } from '@modules/common';
import { isTenants } from '@business/services/common_service/common_service';
import { SortableTaskItem } from './TaskItemOld';
import { TaskItem } from './TaskItem';
import './index.scss';
import { TaskService } from '@business/services';
import { SimplePagination } from '@core/components/SimplePagination';
import { TodosPageBloc } from '@modules/TodosPage/TodosPageBloc';
import { tap } from 'rxjs/operators';

export interface TaskListProps {
  isCompleted?: boolean;
  bloc: TasksMenuBloc;
  assignee?: UserId[];
  onCreate?(id: ChecklistTemplateResponse, isChecklist: boolean): void;
  onAssign(type: string, checklistId: ChecklistId, assignees: string[]): void;
  parentSectionId: string;
  sortBy?: SortByOptions;
  sortByDirection: SortByDirections;
  taskStatuses: TaskStatusResponse[];
  noChecklists?: boolean;
  canCreateTasks?: boolean;
  orgId?: OrgId;
  objectId?: string;
  objectType?: string;
  hasPagination?: boolean;
  showAll?: boolean;
  showSubtasks?: boolean;
}

export const TaskList = memo((props: TaskListProps) => {
  const {
    isCompleted = false,
    bloc,
    assignee,
    onCreate,
    onAssign,
    parentSectionId,
    sortBy = null,
    sortByDirection,
    taskStatuses = [],
    noChecklists = false,
    canCreateTasks = true,
    orgId,
    objectId,
    objectType,
    hasPagination = false,
    showAll = false,
    showSubtasks = false,
  } = props;
  const users = useObservable(bloc.users$, []);
  const teams = useObservable(bloc.teams$, []);
  const allUsers = useObservable(bloc.allUsers$, []);
  const blocTasks = useObservable(bloc.tasks$, []);
  const loading = useLoadingState(bloc.loading);
  const blocInitialChecklists = useObservable(bloc.checklists$, []);
  const blocChecklists = useMemo(
    () =>
      R.filter(blocInitialChecklists, c => {
        if (c.recurring === 'one-time') {
          return true;
        } else {
          return !c?.isParent;
        }
      }),
    [blocInitialChecklists],
  );

  const user = useObservable(Session.user$);
  const history = useHistory();
  const assigneeId = useMemo(() => assignee && assignee.length && R.toNumber(R.replace(R.first(assignee) || '', 'user-', '')), [assignee]);
  const accountSettings = useAccountSettings();
  const isManagement = useMemo(() => accountSettings?.management || false, [accountSettings.management]);
  const taskMeta = useObservable((bloc as any as TodosPageBloc).taskPagination$, null);

  const filter =
    sortBy === SortByOptions.dueDate
      ? 'dueDate'
      : sortBy === SortByOptions.title
      ? 'title'
      : sortBy === SortByOptions.dateAdded
      ? 'createdAt'
      : 'menuOrder';
  const order = sortByDirection;
  const tasks = useMemo(() => {
    const filtered = R.pipe(
      blocTasks,
      R.ops.filter(task => isCompleted === task.isCompleted),
      R.ops.sortBy(task => (!isCompleted ? task.menuOrder : -(task.completedAt || 0))),
    );
    const filteredByAssignee = assigneeId ? filtered.filter(f => f.assignee.indexOf(assigneeId.toString()) > -1) : filtered;
    return R.orderBy(filtered, [filter === 'title' ? t => t.title.toLowerCase() : filter], [order]);
  }, [assigneeId, blocTasks, filter, isCompleted, order]);

  const showOldSortable = useMemo(() => isTenants(['1675191225']), []);
  const showPagination = useMemo(() => hasPagination && taskMeta && tasks.length !== 0 && isCompleted, [hasPagination, taskMeta, tasks, isCompleted]);

  const cheklists = useMemo(() => {
    const filtered = TaskService.getFilteredChecklists(blocChecklists, { isCompleted });
    // const filtered = R.pipe(
    //   blocChecklists,
    //   R.ops.filter(c => isCompleted === c.isCompleted),
    //   R.ops.filter(c => c.hasAttempted || c.status === 'needAction' || c.status === 'overDue'),
    // );
    if (R.isNull(sortBy)) {
      return R.orderBy(filtered, 'status', ['desc']);
    }
    return R.orderBy(filtered, [filter === 'title' ? t => t.title.toLowerCase() : filter], [order]);
  }, [blocChecklists, sortBy, filter, order, isCompleted]);

  const onOldSortEnd = useCallback(
    (sort: SortEnd) => {
      const newArray = R.move(tasks, sort.oldIndex, sort.newIndex);
      const sortedIds = newArray.map((item, index) => ({ id: item.id, menuOrder: index }));
      bloc
        .orderTasks(sortedIds)
        .pipe(
          tap(() =>
            (bloc as any as TodosPageBloc).onTodoReload({
              page: taskMeta.page,
              status: isCompleted ? TodoTaskStatus.completed : TodoTaskStatus.pending,
            }),
          ),
        )
        .subscribe();
    },
    [bloc, tasks],
  );

  const onSortEnd = useCallback(
    (newTasks: any) => {
      //   const newArray = R.move(tasks, sort.oldIndex, sort.newIndex);
      const sortedIds = newTasks.map((item, index) => ({ id: item.id, menuOrder: index }));
      bloc
        .orderTasks(sortedIds)
        .pipe(
          tap(() =>
            (bloc as any as TodosPageBloc).onTodoReload({
              page: taskMeta.page,
              status: isCompleted ? TodoTaskStatus.completed : TodoTaskStatus.pending,
            }),
          ),
        )
        .subscribe();
    },
    [bloc, taskMeta, isCompleted],
  );

  const onUpdate = useCallback(
    (id: TaskId, task: Partial<TaskResponse>) => {
      bloc
        .updateTask(id, task)
        .pipe(
          tap(() =>
            (bloc as any as TodosPageBloc).onTodoReload({
              page: taskMeta.page,
              status: isCompleted ? TodoTaskStatus.completed : TodoTaskStatus.pending,
            }),
          ),
        )
        .subscribe();
    },
    [bloc, taskMeta, isCompleted],
  );

  const onChecklistCreate = useCallback(
    (id: ChecklistTemplateResponse, isChecklist: boolean) => {
      history.push(`/todos/pending/checklist/${id.id}`);
      onCreate && onCreate(id, isChecklist);
    },
    [history, onCreate],
  );

  const onTaskSubmit = useCallback(() => {
    if (assignee) {
      bloc.searchTask(R.first(assignee) || '').subscribe();
    }
  }, [assignee, bloc]);

  const onPageChange = useCallback(
    (page: number) => {
      (bloc as any as TodosPageBloc).onTodoReload({
        page,
        status: isCompleted ? TodoTaskStatus.completed : TodoTaskStatus.pending,
        id: assignee ? R.first(assignee) : undefined,
      });
    },
    [bloc, isCompleted, assignee],
  );

  return (
    <>
      <Card>
        <CardBody>
          {loading.loading && <LoadingIndicator style={{ marginTop: 20, marginBottom: 20 }} />}
          {!tasks.length && !isCompleted && (
            <h4 className="text-muted" style={{ textAlign: 'center', marginBottom: 10, marginTop: 10 }}>
              Hooray! No tasks
            </h4>
          )}

          {showOldSortable && (
            <SortableList lockAxis={isCompleted ? 'x' : 'xy'} distance={2} onSortEnd={sort => onOldSortEnd(sort)} useDragHandle={true}>
              {tasks.map((task, index) => (
                <SortableTaskItem
                  draggable={!sortBy}
                  taskStatuses={taskStatuses}
                  parentSectionId={parentSectionId}
                  isCompleted={isCompleted}
                  key={task.id}
                  index={index}
                  task={task}
                  onUpdate={onUpdate}
                  onRemove={id => bloc.deleteTask(id).subscribe()}
                  onComplete={(id, complete) => bloc.setComplete(id, complete).subscribe()}
                  users={users}
                />
              ))}
            </SortableList>
          )}

          {!showOldSortable && (
            <Sortable
              handle
              lockAxis="y"
              items={tasks}
              getId={c => c.id}
              onSort={onSortEnd}
              renderItem={(task, index, listeners) => (
                <TaskItem
                  listeners={listeners}
                  draggable={!sortBy}
                  taskStatuses={taskStatuses}
                  parentSectionId={parentSectionId}
                  isCompleted={isCompleted}
                  key={task.id}
                  task={task}
                  onUpdate={onUpdate}
                  onRemove={id => bloc.deleteTask(id).subscribe()}
                  onComplete={(id, complete) => bloc.setComplete(id, complete).subscribe()}
                  users={users}
                  showSubtasks={showSubtasks}
                />
              )}
            />
          )}

          {!isCompleted && !!tasks.length && canCreateTasks && (
            <AddTask
              users={users}
              bloc={bloc}
              assignee={assignee}
              user={user}
              parentSectionId={parentSectionId}
              onSubmit={onTaskSubmit}
              statuses={taskStatuses}
              orgId={orgId}
              objectId={objectId}
              objectType={objectType}
            />
          )}
          {showPagination && (
            <div style={{ display: 'flex', justifyContent: 'center', alignSelf: 'center', marginTop: 15 }}>
              <SimplePagination page={taskMeta.page} total={taskMeta.total} onPage={onPageChange} maxPage={10} />
            </div>
          )}
          {showAll && (
            <div
              onClick={() => history.push('/todos/pending#')}
              style={{ display: 'flex', justifyContent: 'center', alignSelf: 'center', marginTop: 15 }}
            >
              <a>Show all</a>
            </div>
          )}
          {!isCompleted && !tasks.length && canCreateTasks && (
            <AddTask
              statuses={taskStatuses}
              users={users}
              bloc={bloc}
              assignee={assignee}
              showTaskForm={true}
              user={user}
              parentSectionId={parentSectionId}
              onSubmit={onTaskSubmit}
              orgId={orgId}
              objectId={objectId}
              objectType={objectType}
            />
          )}
        </CardBody>
      </Card>
      {isManagement && !noChecklists && (
        <Card>
          <CardBody>
            <Row>
              <Col>
                <h5 className="font-size-14 mt-3 ml-4">Checklists</h5>
              </Col>
              <Col className="text-right mb-2">
                <Button
                  intent="primary"
                  extraClassNames="ml-2"
                  onClick={() =>
                    openAddEditChecklistTemplateModal({
                      type: 'todoChecklist',
                      assignee: assignee,
                      onCreate: onChecklistCreate,
                      userChecklist: true,
                    })
                  }
                >
                  Assign Checklist
                </Button>
              </Col>
            </Row>

            {cheklists.map(c => (
              <ChecklistItem
                parentSectionId={parentSectionId}
                key={c.id}
                checklist={c}
                isCompleted={isCompleted}
                teams={teams}
                users={allUsers}
                onAssign={onAssign}
              />
            ))}
            <div className="p-2">
              <a
                className="ml-4 text-muted font-size-10"
                onClick={() =>
                  openAddEditChecklistTemplateModal({
                    type: 'todoChecklist',
                    assignee: assignee,
                    onCreate: onChecklistCreate,
                    userChecklist: true,
                  })
                }
              >
                <i className="fa fa-plus mr-1"></i> New Checklist
              </a>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
});
